import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { REQUIREMENT_TYPES } from "@/lib/content";

const schema = z.object({
  name: z.string().min(2, "Tell us your name"),
  institution: z.string().min(2, "Institution name"),
  phone: z.string().min(6, "Valid phone please"),
  requirement: z.string().min(2, "Pick a requirement"),
});

export default function LeadForm({ compact = false }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { requirement: "roboinsci_program" },
  });
  const [status, setStatus] = useState({ kind: "idle", msg: "" });

  const onSubmit = async (values) => {
    try {
      setStatus({ kind: "idle", msg: "" });
      
      // Construct WhatsApp message
      const message = `Hello Gakuen Team,\n\nI am ${values.name} from ${values.institution}.\n\nRequirement: ${values.requirement}\nPhone: ${values.phone}`;
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/918121642833?text=${encodedMessage}`;
      
      // Immediate redirect
      window.open(whatsappUrl, "_blank");
      setStatus({
        kind: "ok",
        msg: "Redirecting to WhatsApp...",
      });
      reset({ requirement: "roboinsci_program" });
      
    } catch (e) {
      setStatus({ kind: "err", msg: "Something went wrong. Please try again." });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      data-testid="lead-form"
      className={`bg-white border border-[var(--ri-ink)] ${
        compact ? "p-6" : "p-7 md:p-10"
      } shadow-[6px_6px_0_0_var(--ri-ink)]`}
    >
      <div className="flex items-center justify-between mb-6">
        <span className="ri-tag ri-tag--orange">
          <Send size={12} /> Quick Inquiry
        </span>
      </div>

      <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.05]">
        Get in touch via WhatsApp.
      </h3>
      <p className="mt-2 text-[var(--ri-muted)] text-sm">
        Select your requirement and we'll connect with you instantly.
      </p>

      <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="overline block mb-2">Name *</label>
          <input
            data-testid="lead-input-name"
            className="ri-input"
            placeholder="Your name"
            {...register("name")}
          />
          {errors.name && (
            <p className="mt-1 text-[12px] text-[var(--ri-orange)]">
              {errors.name.message}
            </p>
          )}
        </div>

        <div>
          <label className="overline block mb-2">Institution *</label>
          <input
            data-testid="lead-input-institution"
            className="ri-input"
            placeholder="School/College"
            {...register("institution")}
          />
          {errors.institution && (
            <p className="mt-1 text-[12px] text-[var(--ri-orange)]">
              {errors.institution.message}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-2">Phone *</label>
          <input
            data-testid="lead-input-phone"
            className="ri-input"
            placeholder="+91 ·· ··· ····"
            {...register("phone")}
          />
          {errors.phone && (
            <p className="mt-1 text-[12px] text-[var(--ri-orange)]">
              {errors.phone.message}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-2">Requirement *</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {REQUIREMENT_TYPES.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 border border-[var(--ri-ink)] px-3 py-2 cursor-pointer has-[:checked]:bg-[var(--ri-orange)] has-[:checked]:text-white transition-colors"
                data-testid={`lead-requirement-${opt.value}`}
              >
                <input
                  type="radio"
                  value={opt.value}
                  className="accent-[var(--ri-orange)]"
                  {...register("requirement")}
                />
                <span className="font-mono text-[11px] uppercase tracking-[0.12em]">
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
          {errors.requirement && (
            <p className="mt-1 text-[12px] text-[var(--ri-orange)]">
              {errors.requirement.message}
            </p>
          )}
        </div>
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full md:w-auto disabled:opacity-60"
          data-testid="lead-submit"
        >
          {isSubmitting ? "Opening..." : "Send Inquiry"}{" "}
          <ArrowUpRight size={14} />
        </button>
      </div>

      {status.kind === "ok" && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5 flex items-start gap-3 border border-[var(--ri-ink)] p-4 bg-[var(--ri-mint)]/40"
          data-testid="lead-success"
        >
          <CheckCircle2 size={18} className="mt-0.5" />
          <div className="text-sm">{status.msg}</div>
        </motion.div>
      )}
    </form>
  );
}
